import { createContext, createRef, type FC, Fragment, type ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import AIOInput, { AICheckbox, AIFormInput, AINumber, AIRadio, AISelect, AIText, AITime, type I_AIOInput } from "../aio-input";
import * as UT from '../aio-utils';
import usePopup, { type I_usePopup } from "../aio-popup";
import AIODate from "../aio-date";
import "./index.css"
type I_rows<T> = { rows: T[], searchedRows: T[], sortedRows: T[], pagedRows: T[], groupedRows?: I_groupRow[] }
type I_rowOption<T, R> = (p: I_rowDetail<T>) => R
type I_rowDetail<T> = { row: T, rowIndex: number }
export type I_table_column<T> = {
    title?: any,
    sort?: true | I_table_sort<T>,
    filter?: boolean,
    search?: boolean,
    width?: any,
    minWidth?: any,
    id: string,
    excel?: string | boolean,
    justify?: boolean,
    filterInput?: I_AIOInput & { type: I_table_column<any>["type"], label?: string },
    filterOperators?: ('less' | 'lessequal' | 'more' | 'moreequal' | 'equal' | 'notEqual' | 'contain' | 'notContain')[], show?: boolean,
    toggle?: boolean,
    data?: any,
    type?: 'text' | 'number' | 'date' | 'jalaliDate' | 'select' | 'multiSelect' | 'checkbox',
    template?: string | ((p: { row: T, column: I_table_column<T>, rowIndex: number, change: (newRow: T) => void, value: any, dateObject?: { year: number, month: number, day: number, hour: number, minute: number, second: number } }) => ReactNode | undefined),
    before?: string | ((p: { row: T, column: I_table_column<T>, rowIndex: number, change: (newRow: T) => void, value: any, dateObject?: { year: number, month: number, day: number, hour: number, minute: number, second: number } }) => ReactNode | undefined),
    after?: string | ((p: { row: T, column: I_table_column<T>, rowIndex: number, change: (newRow: T) => void, value: any, dateObject?: { year: number, month: number, day: number, hour: number, minute: number, second: number } }) => ReactNode | undefined),
    subtext?: string | ((p: { row: T, column: I_table_column<T>, rowIndex: number, change: (newRow: T) => void, value: any, dateObject?: { year: number, month: number, day: number, hour: number, minute: number, second: number } }) => ReactNode | undefined),
    value?: string | ((p: { row: T, column: I_table_column<T>, rowIndex: number }) => string | undefined),
    pattern?: string,
    affix?: string,
    prefix?: string,
    cellAttrs?: (p: { row: T, column: I_table_column<T>, value: any, rowIndex: number }) => any,
    titleAttrs?: (column: I_table_column<T>) => any
}
type I_table_filterTrans = (key: string) => string
export type I_table_filter_saved = { name: string, data: any }
export type I_table_filter = {
    onChange?: (newFilters: I_table_filter_item[]) => boolean,
    getSavedItems?: () => Promise<{ name: string, data: any }[]>,
    setSavedItems?: (savedItems: { name: string, data: any }[]) => Promise<boolean>,
    items?: I_table_filter_item[],
    modalAttrs?: any,
    cache?: string
}
export type I_table_filter_item = {
    columnId: string, value: any,
    operator?: 'less' | 'lessequal' | 'more' | 'moreequal' | 'equal' | 'notEqual' | 'contain' | 'notContain',
    id: string
}

export type I_table<T> = {
    inlineSort?: boolean,
    fa?: boolean,
    addText?: ReactNode | ((value: any) => ReactNode),
    columns?: I_table_column<T>[],
    excel?: string | ((value: any[]) => any[]),
    headerAttrs?: any,
    onChangePaging?: (newPaging: I_table_paging) => void,
    onChangeSort?: (sorts: I_table_sort<T>[]) => Promise<boolean>,
    onSwap?: (newValue: T[], startRow: T, endRow: T) => void,
    onSearch?: true | ((searchValue: string) => void),
    searchPlaceholder?: string,
    paging?: I_table_paging,
    removeText?: string,
    rowAttrs?: I_rowOption<T, { [attrs: string]: any }>,
    rowBefore?: I_rowOption<T, ReactNode>,
    rowAfter?: I_rowOption<T, ReactNode>,
    rowTemplate?: I_rowOption<T, ReactNode>
    rowsTemplate?: (rows: T[]) => ReactNode,
    toolbar?: ReactNode | (() => ReactNode),
    toolbarAttrs?: any,
    tabIndex?: number,
    value: T[],
    onChange?: (v: T[]) => void,
    className?: string,
    style?: any,
    attrs?: any,
    placeholder?: ReactNode,
    filter?: I_table_filter,
    gap?: [number, number],
    group?: {
        getKeyValues: (row: any) => {groupKey:string,groupValue:any}[]
        getCard: (p: I_groupData) => ReactNode | string
    },
    striped?: [string, string],
    onChangeColumns?: (v: I_table_column<any>[]) => void,
    columnOption?: {
        [key in keyof I_table_column<T>]?: (p: { column: I_table_column<T>, row: T, rowIndex: number }) => any
    },
    templates?: { [key: string]: (p: { row: T, column: I_table_column<T>, rowIndex: number, change: (newRow: T) => void, value: any, dateObject?: { year: number, month: number, day: number, hour: number, minute: number, second: number } }) => ReactNode; }
    cellAttrs?: (p: { row: T, column: I_table_column<T>, value: any, rowIndex: number }) => any,
    titleAttrs?: (column: I_table_column<T>) => any,
}
type I_groupsOpen = { [key: string]: boolean | undefined }
type I_context<T> = {
    popup: I_usePopup,
    rootProps: I_table<T>,
    ROWS: I_rows<T>,
    search: (searchValue: string) => void,
    exportToExcel: () => void,
    sortHook: ReturnType<typeof useSort<T>>,
    excelColumns: I_table_column<T>[],
    filterColumns: I_table_column<T>[],
    toggleColumns: I_table_column<T>[],
    tableHook: ReturnType<typeof useTable<T>>,
    DragColumns: UT.I_useDrag,
    pagingHook: ReturnType<typeof usePaging<T>>,
    columns: I_table_column<T>[],
    groupsHook: ReturnType<typeof useGroups<T>>
}

const Context = createContext<I_context<any>>({} as any);
const Provider = <T,>(p: { value: I_context<T>, children: ReactNode }) => <Context.Provider value={p.value}>{p.children}</Context.Provider>
const useProvider = () => useContext(Context)
const AIOTable = <T,>(props: I_table<T>) => {
    const popup = usePopup()
    const fileClass = new UT.FileClass()
    let [searchValue, setSearchValue] = useState<string>('')
    const propsRef = useRef<I_table<T>>(props)
    propsRef.current = props;
    const pagingHook = usePaging({ rows: props.value, paging: props.paging, onChange: props.onChangePaging })
    // const getColumnOption: I_getColumnOption<any> = (key, cellDetails) => {
    //     if (key === 'id') { return cellDetails.column.id }
    //     const columnValue = cellDetails.column[key]
    //     if (columnValue !== undefined) {
    //         const { getValue = {} } = props
    //         if (getValue[columnValue]) { return getValue[columnValue](cellDetails) }
    //         return cellDetails.column[key]
    //     }
    //     const { columnOption } = props;
    //     const fn = (columnOption || {})[key] || (() => { })
    //     const res = fn(cellDetails);
    //     if (res !== undefined) { return res }
    //     if (key === 'value') {
    //         return cellDetails.row[cellDetails.column.id]
    //     }
    //     const defaults: { [key in keyof I_table_column<any>]?: any } = { show: true, title: '', justify: false, titleAttrs: {} }
    //     return defaults[key]
    // }
    const tableHook = useTable<T>(() => propsRef.current, () => props.paging)
    const groupsHook = useGroups<T>(props.group)
    const getColumn = (col: I_table_column<T>): I_table_column<T> => {
        const { columnOption = {} } = props;
        const column: I_table_column<T> = { ...col }
        for (let prop in columnOption) {
            if (prop !== 'value') {
                const fn = columnOption[prop as keyof I_table_column<T>]
                if (fn) {
                    const res = fn({ column, row: undefined as any, rowIndex: 0 })
                    if (res !== undefined) { column[prop as keyof I_table_column<T>] = res }
                }
            }
        }
        const res = { ...column, ...col }
        if (res.value === undefined) { res.value = `row.${column.id}` }
        return res
    }
    const getColumns = () => {
        let { columns = [] } = props;
        let searchColumns: I_table_column<T>[] = [], excelColumns: I_table_column<T>[] = [], filterColumns: I_table_column<T>[] = [], toggleColumns: I_table_column<T>[] = [];
        let updatedColumns = columns.map((col: I_table_column<T>, i: number) => {
            const column = getColumn(col)
            const { filter, search, excel, toggle, id, show = true } = column;
            if (id === undefined) { console.error(`aio-table error => missing column id in props.columns[${i}]`) }
            const fixedColumn = { ...column, show }
            if (search) { searchColumns.push(fixedColumn) }
            if (excel) { excelColumns.push(fixedColumn) }
            if (filter) { filterColumns.push(fixedColumn) }
            if (toggle) { toggleColumns.push(fixedColumn) }
            return fixedColumn
        })
        return { columns: updatedColumns, searchColumns, excelColumns, filterColumns, toggleColumns }
    }
    const DragColumns = UT.useDrag((dragIndex, dropIndex, reOrder) => {
        const newColumns = reOrder(props.columns || [], dragIndex, dropIndex)
        if (props.onChangeColumns) { props.onChangeColumns(newColumns) }
    }, 'aio-table-title')
    const getSorts = (columns: I_table_column<T>[]): I_table_sort<T>[] => {
        let sorts: I_table_sort<T>[] = [];
        for (let i = 0; i < columns.length; i++) {
            const column = columns[i];
            const columnId = column.id
            const sortValue = column.sort
            const sort = sortValue === true ? { sortId: columnId as string } as I_table_sort<T> : sortValue;
            if (!sort) { continue }
            const { type = 'text' } = column;
            let { active = false, dir = 'dec', sortId, order = Infinity } = sort as I_table_sort<T>;
            let sortItem: I_table_sort<T> = {
                isDate: type === 'date' || type === 'jalaliDate',
                sortId, active, dir, columnId,
                title: sort.title || column.title, order,
                getValue: (row: T, dir: 'inc' | 'dec') => {
                    if (sort.getValue) { return sort.getValue(row, dir) }
                    const cellValue = tableHook.getCellValue({ row, column, rowIndex: 0 })
                    if (column.type === 'number' && typeof cellValue !== 'number') { return dir === 'inc' ? Infinity : -Infinity }
                    return cellValue
                }
            }
            sorts.push(sortItem)
        }
        return UT.SortArray(sorts, [{ getValue: (o) => o.order, dir: 'inc' }])
    }
    const sortHook = useSort<T>({
        fa: props.fa,
        sorts: [],
        rows: propsRef.current.value,
        onChangeRows: props.onChange,
        onChangeSort: props.onChangeSort,
    })

    useEffect(() => {
        sortHook.setSorts(getSorts(columns));
    }, [])
    useEffect(() => {
        if (props.paging) {
            pagingHook.changePaging(props.paging)
        }
    }, [JSON.stringify(props.paging)])
    function exportToExcel() {
        let list = [];
        if (typeof props.excel === 'function') {
            list = props.excel(props.value)
        }
        else {
            for (let rowIndex = 0; rowIndex < props.value.length; rowIndex++) {
                let row = props.value[rowIndex], json: any = {};
                for (let j = 0; j < excelColumns.length; j++) {
                    const column = excelColumns[j];
                    const { excel, title } = column;
                    let key: string = '';
                    if (excel === true) {
                        if (typeof title === 'string') { key = title }
                        else { key = 'untitle' }
                    }
                    else if (typeof excel === 'string') { key = excel }
                    else { continue }
                    json[key] = tableHook.getCellValue({ row, rowIndex, column })
                }
                list.push(json)
            }
        }
        fileClass.ExportToExcel(list, { promptText: typeof props.excel === 'string' ? props.excel : 'Inter Excel File Name' })
    }
    function getSearchedRows(rows: T[]): T[] {
        if (props.onSearch !== true) { return rows }
        if (!searchColumns.length || !searchValue) { return rows }
        return UT.Search(rows, searchValue, (row, rowIndex) => {
            let str = '';
            for (let i = 0; i < searchColumns.length; i++) {
                let column = searchColumns[i];
                let cellValue = tableHook.getCellValue({ row, rowIndex, column });
                if (cellValue) { str += cellValue + ' ' }
            }
            return str
        })
    }

    function getRows(): I_rows<T> {
        const rows: T[] = props.value;
        let searchedRows = getSearchedRows(rows);
        let sortedRows = sortHook.getSortedRows(searchedRows);
        let pagedRows = pagingHook.getPagedRows(sortedRows)
        const groupedRows = groupsHook.getGroupedRows(pagedRows)
        return { rows: props.value, searchedRows, sortedRows, pagedRows, groupedRows }
    }
    function search(searchValue: string) {
        if (props.onSearch === true) { setSearchValue(searchValue) }
        else if (typeof props.onSearch === 'function') { props.onSearch(searchValue) }
    }
    const { columns, searchColumns, excelColumns, filterColumns, toggleColumns } = useMemo(() => getColumns(), [props.columns]);
    let ROWS: I_rows<T> = getRows();
    const context: I_context<T> = {
        rootProps: props, excelColumns, filterColumns, tableHook, sortHook, ROWS, toggleColumns, pagingHook,
        search, exportToExcel, DragColumns, popup, columns, groupsHook
    }
    return (
        <Provider value={context}>
            <AIOTableComponent {...props} />
            {popup.render()}
        </Provider>
    )
}
export default AIOTable
const AIOTableComponent = <T,>(props: I_table<T>) => {
    const { pagingHook, rootProps, ROWS } = useProvider()
    const { filter } = rootProps;
    let [dom] = useState(createRef())
    const { gap = [] } = props;
    let attrs = UT.AddToAttrs(props.attrs, { className: ['aio-table', props.className], style: { gap: gap[1], ...props.style }, attrs: { ref: dom } })
    const filterHook = useFilter()
    return (
        <div {...attrs}>
            <TableToolbar filterHook={filterHook} />
            {!!filter && filterHook.renderTags()}
            <div className='aio-table-unit aio-table-scroll' style={{ gap: gap[1] }}>
                <TableHeader<T> />
                {!ROWS.groupedRows && <TableRows<T> rows={ROWS.pagedRows || []} />}
                {!!ROWS.groupedRows && <TableGroups data={ROWS.groupedRows} level={0} groupKeys={[]} groupValues={[]} isParentLastChild={false}/>}
            </div>
            {pagingHook.render()}
        </div>
    )
}
const TableRows = <T,>({ rows }: { rows: any[] }) => {
    let { rootProps } = useProvider()
    let { rowBefore = () => null, rowAfter = () => null, rowTemplate, rowsTemplate, placeholder = 'there is not any items' } = rootProps;
    let content;
    if (rowsTemplate) { content = rowsTemplate(rows) }
    else if (rows.length) {
        if (rootProps.onSwap) {
            content = (
                <UT.DragAndDrop
                    data={rows}
                    getId={(o) => {
                        let { id = 'ailr' + Math.round(Math.random() * 10000000) } = o;
                        o._id = o._id === undefined ? id : o._id;
                        return o._id
                    }}
                    getContent={(o, { index, id }) => {
                        o._id = o._id === undefined ? id : o._id;
                        const rowDetail: I_rowDetail<T> = { row: o, rowIndex: index }
                        let Row;
                        if (rowTemplate) { Row = rowTemplate(rowDetail) }
                        else { Row = <TableRow<T> key={o._id} rowDetail={rowDetail} /> }
                        return (<Fragment key={o._id}>{rowBefore(rowDetail)}{Row}{rowAfter(rowDetail)}</Fragment>)
                    }}
                    onChange={(newRows) => {
                        if (rootProps.onChange) { rootProps.onChange(newRows) }
                    }}
                />
            )
        }
        else {
            content = rows.map((o, i) => {
                let { id = 'ailr' + Math.round(Math.random() * 10000000) } = o;
                o._id = o._id === undefined ? id : o._id;
                const rowDetail: I_rowDetail<T> = { row: o, rowIndex: i }
                let Row;
                if (rowTemplate) { Row = rowTemplate(rowDetail) }
                else { Row = <TableRow<T> key={o._id} rowDetail={rowDetail} /> }
                return (<Fragment key={o._id}>{rowBefore(rowDetail)}{Row}{rowAfter(rowDetail)}</Fragment>)
            })
        }
    }
    else if (placeholder) {
        content = <div style={{ width: '100%', textAlign: 'center', padding: 12, boxSizing: 'border-box' }}>{placeholder}</div>
    }
    else { return null }
    const { gap = [] } = rootProps;
    return <div className='aio-table-rows' style={{ gap: gap[1] }}>{content}</div>
}
const TableToolbar: FC<{ filterHook: ReturnType<typeof useFilter> }> = ({ filterHook }) => {
    let { exportToExcel, search, rootProps, excelColumns, toggleColumns, sortHook, columns } = useProvider();
    let { toolbarAttrs, toolbar, onSearch, onChangeColumns, fa, filter, inlineSort, searchPlaceholder = 'Search Here...' } = rootProps;
    const columnsRef = useRef<I_table_column<any>[]>(columns)
    columnsRef.current = columns;
    toolbarAttrs = UT.AddToAttrs(toolbarAttrs, { className: 'aio-table-toolbar' })
    if (!toolbar && !onSearch && !sortHook.sorts.length && !excelColumns.length && !toggleColumns.length && !filter) { return null }

    // function getAddText() {
    //     let { addText } = rootProps;
    //     if (!rootProps.addText) { return new UT.GetSvg('em').getIcon('mdiPlusThick', 0.8) }
    //     return typeof addText === 'function' ? addText(value) : addText
    // }
    return (
        <div {...toolbarAttrs}>
            {toolbar && <div className='aio-table-toolbar-content'>{typeof toolbar === 'function' ? toolbar() : toolbar}</div>}
            {!!filter && filterHook.renderButton()}
            {!!onSearch && <AIOInput placeholder={searchPlaceholder} type='text' className='aio-table-search' onChange={(value) => search(value)} after={new UT.GetSvg('em').getIcon('mdiMagnify', 0.7)} />}
            {!!sortHook.sorts.length && !inlineSort && sortHook.renderSortButton({ className: 'aio-table-toolbar-button aio-table-sort-button' })}
            {!!excelColumns.length && <div className='aio-table-toolbar-button aio-table-excel-button' onClick={() => exportToExcel()}>{new UT.GetSvg('em').getIcon('mdiFileExcel', 0.8)}</div>}
            {
                !!toggleColumns.length &&
                <AISelect
                    popover={{
                        header: { title: fa ? 'نمایش ستون ها' : 'Show Columns', onClose: false },
                        headerAttrs: { className: 'aio-table-dropdown-header' },
                        limitTo: '.aio-table'
                    }}
                    className='aio-table-toolbar-button aio-table-toggle-button'
                    text={new UT.GetSvg('em').getIcon('mdiEye', 0.8)} justify={true}
                    hideTags={true}
                    options={toggleColumns.map((o) => ({ ...o }))}
                    caret={false}
                    option={{
                        text: (column: I_table_column<any>) => column.title,
                        checked: (column: I_table_column<any>) => !!column.show,
                        onClick: (column: I_table_column<any>) => {
                            console.log(toggleColumns)
                            if (!onChangeColumns) { return }
                            const columns = columnsRef.current || []
                            const newColumns = columns.map((o) => column.id === o.id ? { ...o, show: !o.show } : o)
                            onChangeColumns(newColumns)
                        },
                        show: () => true,
                        justify: () => false,
                        close: () => false
                    }}
                />
            }
        </div>
    )
}
const TableHeader = <T,>() => {
    let { rootProps, columns } = useProvider();
    let { headerAttrs, gap = [] } = rootProps;
    headerAttrs = UT.AddToAttrs(headerAttrs, { className: 'aio-table-header', style: { gap: gap[0] } })
    let Titles = columns.map((column, i) => {
        const columnId = column.id
        return <TableTitle<T> key={columnId} column={column} isLast={i === columns.length - 1} colIndex={i} />
    });
    return <div {...headerAttrs}>{Titles}</div>
}
const TableTitle = <T,>(p: { column: I_table_column<T>, isLast: boolean, colIndex: number }) => {
    const { column, colIndex } = p;
    let { tableHook, DragColumns, sortHook, rootProps } = useProvider();
    const { show = true, title } = column;
    if (!show) { return null }
    const attrs = {
        ...tableHook.getTitleAttrs(column),
        ...DragColumns.getDragAttrs(colIndex), ...DragColumns.getDropAttrs(colIndex),
    }
    return (
        <div {...attrs}>
            {title}
            {!!rootProps.inlineSort && sortHook.renderSortArrow(sortHook.getSortByColumn(column), true)}
        </div>
    )
}
const TableRow = <T,>(props: { rowDetail: I_rowDetail<T> }) => {
    const { rowDetail } = props;
    const { row, rowIndex } = rowDetail;
    const rowId = (row as any)._id
    let { tableHook, columns } = useProvider();
    function getCells() {
        return columns.map((column) => {
            const { show = true } = column;
            if (!show) { return null }
            const columnId = column.id
            const key = rowId + ' ' + columnId;
            return (<TableCell<T> key={key} row={row} rowIndex={rowIndex} column={column} />)
        })
    }
    return (<div key={rowId} {...tableHook.getRowAttrs({ row, rowIndex })}>{getCells()}</div>)
}
const TableCell = <T,>(props: { row: T, column: I_table_column<T>, rowIndex: number }) => {
    let { row, column, rowIndex } = props;
    const { tableHook } = useProvider();
    const { type = 'text', id } = column;
    let cellValue: any = tableHook.getCellValue({ row, rowIndex, column })
    if (type === 'date') { cellValue = new AIODate().toObject(cellValue as any) }
    else if (type === 'jalaliDate') { cellValue = new AIODate().toObject(cellValue, true) }
    const rowId = (row as any)._id;
    const colId = id;
    const getTemplateValue = () => {
        if (column.type === 'date') { try { cellValue = new AIODate().toObject(cellValue) } catch { } }
        else if (column.type === 'jalaliDate') { try { cellValue = new AIODate().toObject(cellValue, true) } catch { } }
        const cellTemplateValue = tableHook.getCellTemplateValue({ row, column, rowIndex, key: 'template' })
        if (cellTemplateValue !== undefined) { return cellTemplateValue }
        return tableHook.getCellPatternValue({ row, column, rowIndex })
    }
    const templateValue = getTemplateValue();
    const before = tableHook.getCellTemplateValue({ row, column, rowIndex, key: 'before' })
    const after = tableHook.getCellTemplateValue({ row, column, rowIndex, key: 'after' })
    const subtext = tableHook.getCellTemplateValue({ row, column, rowIndex, key: 'subtext' })
    let content;
    if (column.template) {
        content = tableHook.getCellTemplateValue({ row, column, rowIndex, key: 'template' })
    }
    else if (column.pattern) { content = tableHook.getCellPatternValue({ row, column, rowIndex }) }
    if (templateValue !== undefined) { content = templateValue }
    else if (column.type === 'number') { content = UT.SplitNumber(cellValue) }
    else { content = cellValue; }
    content = content !== undefined && content !== null && content !== '' ? <>{column.prefix ?? null}{content}{column.affix ?? null}</> : content
    return (
        <Fragment key={rowId + ' ' + colId}>
            <div {...tableHook.getCellAttrs({ row, column, rowIndex })}>
                {before !== undefined && <div className="aio-table-cell-before">{before}</div>}
                <div className={`aio-table-cell-value${subtext !== undefined ? ' has-subtext' : ''}`} data-subtext={subtext}>{content}</div>
                {after !== undefined && <div className="aio-table-cell-after">{after}</div>}
            </div>
        </Fragment>
    )
}
const useTable = <T,>(getProps: () => I_table<T>, getPaging: () => I_table_paging | undefined) => {
    const fixIndexByPaging = (rowIndex: number) => {
        const paging = getPaging()
        if (paging) {
            let { number, size } = paging;
            return rowIndex + ((number - 1) * size)
        }
        return rowIndex
    }
    const getCellValue = (p: { row: T, rowIndex: number, column: I_table_column<T> }) => {
        let { row, column, rowIndex } = p;
        if (typeof column.value === 'string') {
            let result = column.value;
            if (result.indexOf('row.') !== -1) { try { eval(`result = ${result}`); } catch { } }
            return result
        }
        if (typeof column.value === 'function') { return column.value({ row, rowIndex, column }) }
        if (typeof column.value === 'undefined') {
            const { columnOption } = getProps()
            if (columnOption?.value) {
                return columnOption.value({ row, column, rowIndex: fixIndexByPaging(rowIndex) })
            }
        }
    }
    const getTemplateParam = (p: { row: T, rowIndex: number, column: I_table_column<T> }) => {
        const value = getCellValue(p)
        let dateObject = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
        if (p.column.type === 'date') { try { dateObject = new AIODate().toObject(value as any) } catch { } }
        else if (p.column.type === 'jalaliDate') { try { dateObject = new AIODate().toObject(value as any, true) } catch { } }
        const rowIndex = fixIndexByPaging(p.rowIndex)
        const change = (newRow: T) => changeCell(p.row, newRow)
        return { value, dateObject, rowIndex, row: p.row, column: p.column, change }
    }
    const getCellTemplateValue = (p: { row: T, rowIndex: number, column: I_table_column<T>, key: 'template' | 'before' | 'after' | 'subtext' }) => {
        let { row, rowIndex, column, key } = p;
        const { templates } = getProps();

        if (!column[key]) { return }
        const templateParam = getTemplateParam({ row, column, rowIndex })
        if (typeof column[key] === 'function') {
            const fn: any = column[key] as any
            return fn(templateParam)
        }
        if (typeof column[key] === 'string') {
            if (templates && templates[column[key] as string]) {
                const fn: any = templates[column[key] as string] as any
                return fn(templateParam)
            }
            else {
                return column[key]
            }
        }
    }
    const getCellPatternValue = (p: { row: T, rowIndex: number, column: I_table_column<T> }) => {
        let { row, rowIndex, column } = p;
        const cellValue = getCellValue({ row, column, rowIndex })
        const { pattern } = column
        if (pattern && cellValue !== undefined) {
            try {
                if (column.type === 'date') { return new AIODate().getDateByPattern(cellValue, pattern) }
                else if (column.type === 'jalaliDate') { return new AIODate().getDateByPattern(cellValue, pattern, true) }
                else { return pattern.replace('{value}', cellValue); }
            }
            catch { }
        }
    }
    function changeCell(row: T, newRow: T) {
        if (newRow) { }
        const { onChange, value: propsValue } = getProps()
        if (!onChange) { return }
        const rowId = (row as any)._id;
        onChange(propsValue.map((o: any) => o._id !== rowId ? o : newRow))
    }
    const getCellAttrs = (p: { row: T, rowIndex: number, column: I_table_column<T> }) => {
        const { row, rowIndex, column } = p;
        const { cellAttrs } = getProps();
        const cellValue = getCellValue({ row, column, rowIndex })
        const propsCellAttrs = cellAttrs ? cellAttrs({ row, rowIndex: fixIndexByPaging(rowIndex), column, value: cellValue }) : {}
        const columnCellAttrs = column.cellAttrs ? column.cellAttrs({ row, rowIndex: fixIndexByPaging(rowIndex), column, value: cellValue }) : {}
        const attrs = { ...propsCellAttrs, ...columnCellAttrs }
        const { justify, width, minWidth } = column;
        const className = `aio-table-cell` + (justify ? ` aio-table-cell-justify` : '')
        let isNumber = false
        try {
            if (typeof cellValue === 'number' || column.type === 'number') { isNumber = true }
            else if (typeof cellValue === 'string' && !isNaN(+cellValue)) { isNumber = true }
        }
        catch { }
        const style: any = { width, minWidth, flex: width ? undefined : 1, direction: isNumber ? 'ltr' : undefined }
        return UT.AddToAttrs(attrs, { className: [className, rowIndex % 2 === 0 ? 'aio-table-cell-odd' : 'aio-table-cell-even'], style, attrs: { title: typeof cellValue === 'string' ? cellValue : undefined } });
    }
    const getTitleAttrs = (column: I_table_column<T>) => {
        const { justify, width, minWidth, title } = column;
        const { titleAttrs } = getProps()
        const propsTitleAttrs = titleAttrs ? titleAttrs(column) : {}
        const columnTitleAttrs = column.titleAttrs ? column.titleAttrs(column) : {}
        const attrs = { ...propsTitleAttrs, ...columnTitleAttrs }
        const className = `aio-table-title` + (justify ? ` aio-table-title-justify` : '')
        const style = { width, minWidth, flex: width ? undefined : 1 }
        return UT.AddToAttrs(attrs, { className, style, attrs: { title: typeof title === 'string' ? title : undefined } });
    }
    const getRowAttrs = (p: { row: T, rowIndex: number }) => {
        const { row, rowIndex } = p;
        const { onSwap, gap = [], rowAttrs } = getProps();
        const attrs = rowAttrs ? rowAttrs({ row, rowIndex }) : {};
        let obj = UT.AddToAttrs(attrs, { className: ['aio-table-row', rowIndex % 2 === 0 ? 'aio-table-row-odd' : 'aio-table-row-even'], style: { gap: gap[0] } })
        if (onSwap) {
            obj = {
                ...obj,
            }
        }
        return obj;
    }
    return { getCellValue, getCellTemplateValue, getCellPatternValue, getCellAttrs, getTitleAttrs, getRowAttrs, changeCell }
}
export type I_table_sort<T> = {
    active?: boolean,
    dir?: 'dec' | 'inc',
    title?: ReactNode,
    sortId?: string,
    getValue?: (row: T, dir: 'inc' | 'dec') => any,
    isDate?: boolean,
    columnId?: string,
    order?: number
}
export const useSort = <T,>(p: { fa?: boolean, sorts: I_table_sort<T>[], rows: any[], onChangeRows?: (rows: any) => void, onChangeSort?: (sorts: I_table_sort<T>[]) => Promise<boolean | undefined> }) => {
    let [sorts, setSorts] = useState<I_table_sort<T>[]>(p.sorts)
    const isInitSortExecutedRef = useRef<boolean>(false)
    const getActiveSorts = (sorts: I_table_sort<T>[]): I_table_sort<T>[] => {
        let activeSorts = (sorts || []).filter((sort) => sort.active !== false);
        return activeSorts.map((o) => ({ getValue: o.getValue as any, dir: o.dir }))
    }
    const getSortedRows = (rows: T[]): T[] => {
        if (isInitSortExecutedRef.current) { return rows }
        if (p.onChangeSort) { return rows }
        let activeSorts = getActiveSorts(sorts)
        if (!activeSorts.length || !rows.length) { return rows }
        isInitSortExecutedRef.current = true;
        let sortedRows = UT.SortArray<T>(rows, activeSorts);
        if (p.onChangeRows) { p.onChangeRows(sortedRows); }
        return sortedRows;
    }

    const changeSort = (sortId: string, changeObject: Partial<I_table_sort<T>>) => {
        let newSorts = sorts.map((sort) => {
            if (sort.sortId === sortId) {
                let newSort = { ...sort, ...changeObject }
                return newSort;
            }
            return sort
        });
        changeSorts(newSorts)
    }
    const changeSorts = async (sorts: I_table_sort<T>[]): Promise<void> => {
        if (p.onChangeSort) {
            let res = await p.onChangeSort(sorts.filter((o) => !!o.active))
            if (res !== false) { setSorts(sorts); }
        }
        else {
            setSorts(sorts);
            const activeSorts = getActiveSorts(sorts);
            if (activeSorts.length && !!p.onChangeRows) {
                p.onChangeRows(UT.SortArray(p.rows, activeSorts))
            }
        }
    }
    const renderSortArrow = (option: I_table_sort<T> | false, inline?: boolean) => {
        if (option === false) { return null }
        let { dir = 'dec', sortId, active } = option;
        let path = dir === 'dec' ? 'mdiArrowDown' : 'mdiArrowUp'
        if (inline && !active) {
            path = 'mdiSortVariantOff'
        }

        return (
            <div className='aio-sort-arrow' onClick={(e) => {
                e.stopPropagation();
                if (!sortId) { return }
                let newActive = active, newDir = dir;
                if (inline) {
                    if (active) {
                        if (dir === 'inc') {
                            newDir = 'dec';
                            newActive = true;
                        }
                        else {
                            newActive = false
                        }
                    }
                    else {
                        newDir = 'inc';
                        newActive = true
                    }
                }
                else {
                    newDir = dir === 'dec' ? 'inc' : 'dec'
                }
                changeSort(sortId, { dir: newDir, active: newActive })
            }}>
                {new UT.GetSvg('em').getIcon(path, 0.8)}
            </div>
        )
    }
    const getSortByColumn = (column: I_table_column<T>): I_table_sort<T> | false => {
        const sort = column.sort;
        if (!sort) { return false }
        const sortId = sort === true ? column.id : (sort.sortId === undefined ? column.id : sort.sortId)
        const res = sorts.find((o) => o.sortId === sortId)
        return res === undefined ? false : res
    }
    const renderSortButton = (attrs?: any, limitTo?: string) => {
        if (!sorts.length) { return null }
        const Attrs = UT.AddToAttrs(attrs, { className: 'aio-sort-button' })
        return (
            <AISelect
                key='sortbutton' caret={false} options={sorts} justify={true}
                option={{
                    text: (option) => option.title, checked: (option) => !!option.active, close: () => false, value: (option) => option.sortId,
                    after: (option) => renderSortArrow(option),
                    onClick: (option: I_table_sort<T>) => changeSort(option.sortId || '', { active: !option.active })
                }}
                popover={{
                    header: { title: p.fa ? 'مرتب سازی' : 'Sort', onClose: false },
                    headerAttrs: { className: 'aio-sort-header' },
                    limitTo: limitTo || '.aio-table'
                }}
                attrs={Attrs}
                text={new UT.GetSvg('em').getIcon('mdiSort', 0.7)}
                onSwap={(newSorts) => changeSorts(newSorts)}
            />
        )
    }
    return { sorts, setSorts, renderSortButton, getSortedRows, changeSort, changeSorts, renderSortArrow, getSortByColumn }
}

export type I_table_paging = { serverSide?: boolean, number: number, size: number, length?: number, sizes?: number[] }
export type I_pagingHook<T> = {
    render: () => ReactNode;
    getPagedRows: (rows: T[]) => T[];
    changePaging: (obj: Partial<I_table_paging>) => void
}
const usePaging = <T,>(p: { rows: T[], paging?: I_table_paging, onChange?: (newPaging: I_table_paging) => void }): I_pagingHook<T> => {
    const timeoutRef = useRef<any>(undefined)
    const startRef = useRef<any>(undefined)
    const endRef = useRef<any>(undefined)
    const pagesRef = useRef<any>(undefined)
    const getPaging = (paging?: I_table_paging) => {
        return paging ? fix(paging) : undefined
    }

    function fix(paging: I_table_paging): I_table_paging {
        if (typeof p.onChange !== 'function') {
            alert('aio-table error => in type table you set paging but forget to set onChangePaging function prop to aio input')
            return { number: 0, size: 0 };
        }
        let { number, size = 20, length = 0, sizes = [1, 5, 10, 15, 20, 30, 50, 70, 100], serverSide } = paging
        if (!serverSide) {
            length = p.rows.length
        }
        if (sizes.indexOf(size) === -1) { size = sizes[0] }
        let pages = Math.ceil(length / size);
        number = number > pages ? pages : number;
        number = number < 1 ? 1 : number;
        let start = number - 3, end = number + 3;
        startRef.current = start; endRef.current = end; pagesRef.current = pages;
        return { ...paging, length, number, size, sizes }
    }

    const changePaging = (obj: Partial<I_table_paging>) => {
        const paging = getPaging(p.paging)
        if (!paging) { return }
        let newPaging: I_table_paging = fix({ ...paging, ...obj });
        if (p.onChange) {
            if (newPaging.serverSide) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => {
                    //be khatere fahme payine typescript majbooram dobare in shart ro bezanam
                    if (p.onChange) { p.onChange(newPaging) }
                }, 800);
            }
            else { p.onChange(newPaging) }
        }
    }
    const getPagedRows = (rows: T[]) => {
        const paging = getPaging(p.paging)
        if (!paging || paging.serverSide) { return rows }
        const { size, number } = paging
        return rows.slice((number - 1) * size, number * size)
    }
    function changeSizeButton(sizes: number[], size: number) {
        if (!sizes.length) { return null }
        return (
            <AISelect
                attrs={{ className: 'aio-paging-button aio-paging-size' }} value={size}
                options={sizes} option={{ text: (o) => o, value: (o) => o }} justify={true}
                onChange={(value) => changePaging({ size: value as any })} popover={{ fitHorizontal: true }}
            />
        )
    }
    const render = () => {
        const paging = getPaging(p.paging)
        if (!paging) { return null }
        if (!p.rows.length) { return null }
        let { number, size, sizes } = paging;
        let buttons = [];
        let isFirst = true
        for (let i = startRef.current; i <= endRef.current; i++) {
            if (i < 1 || i > pagesRef.current) {
                buttons.push(<button key={i} className={'aio-paging-button aio-paging-button-hidden'}>{i}</button>)
            }
            else {
                let index: number;
                if (isFirst) { index = 1; isFirst = false; }
                else if (i === Math.min(endRef.current, pagesRef.current)) { index = pagesRef.current }
                else { index = i; }
                buttons.push(<button key={index} className={'aio-paging-button' + (index === number ? ' active' : '')} onClick={() => changePaging({ number: index })}>{index}</button>)
            }
        }
        return (
            <div className='aio-paging'>
                {buttons}
                {changeSizeButton(sizes || [], size)}
            </div>
        )
    }
    return { render, getPagedRows, changePaging }
}

type I_table_filterContext = {
    trans: I_table_filterTrans,
    popup: I_usePopup,
    filterDataHook: ReturnType<typeof useFilterData>,
    rootProps: I_table<any>
}
const FilterContext = createContext<I_table_filterContext>({} as any)
const FilterContextProvider = (props: { children: ReactNode, value: I_table_filterContext }) => <FilterContext.Provider value={props.value}>{props.children}</FilterContext.Provider>
const useFilterContext = () => useContext(FilterContext)
const useFilter = () => {
    const { rootProps } = useProvider()
    const trans: I_table_filterTrans = (key) => {
        const { fa } = rootProps;
        const dic: { [key: string]: string } = {
            'less': fa ? 'کوچک تر از' : 'less than',
            'lessequal': fa ? 'کوچک تر مساوی' : 'equal or less than',
            'more': fa ? 'بزرگتر از' : 'more than',
            'moreequal': fa ? 'بزرگتر مساوی' : 'equal or more than',
            'equal': fa ? 'برابر' : 'equal',
            'notEqual': fa ? 'مخالف' : 'not equal',
            'contain': fa ? 'شامل باشد' : 'contain',
            'notContain': fa ? 'شامل نباشد' : 'not contain',
            'add': fa ? 'افزودن' : 'Add',
            'save': fa ? 'ذخیره' : 'Save',
            'edit': fa ? 'ویرایش' : 'Edit',
            'filters': fa ? 'افزودن فیلتر' : 'Add Filter',
            'remove': fa ? 'حذف' : 'Remove',
            'activate': fa ? 'اعمال' : 'Activate',
            'saved filters': fa ? 'مجموعه فیلتر های ذخیره شده' : 'Saved Filters',
            'current filters': fa ? 'فیلتر های جاری' : 'Current Filter',
            'remove saved filter': fa ? 'حذف فیلتر ذخیره شده' : 'Remove saved filter',
            'remove confirm': fa ? 'از حذف این آیتم اطمینان دارید؟' : 'are you sure to remove this item?',
            'cansel': fa ? 'لغو' : 'Cansel',
            'activate saved filter': fa ? 'اعمال فیلتر ذخیره شده' : 'activate saved filter',
            'activate confirm': fa ? 'از فعالسازی این آیتم اطمینان دارید؟' : 'are you sure to activate this item?',
            'saved empty': fa ? "مجموعه فیلتر ذخیره شده ای موجود نیست" : "There is not any saved filters",
            'inter filter name': fa ? 'نام فیلتر را برای ذخیره وارد کنید' : 'please inter filter name',
            'save filter': fa ? 'ذخیره فیلتر' : 'Save Filter'
        }
        return dic[key]
    }
    const popup = usePopup({ rtl: !!rootProps.fa })
    const filterDataHook = useFilterData(popup, trans)

    const renderButton = (attrs?: any) => {
        return (
            <FilterContextProvider value={{ trans, popup, filterDataHook, rootProps }}>
                <FilterButton attrs={attrs} />
                {popup.render()}
            </FilterContextProvider>
        )
    }
    const renderTags = () => {
        return (
            <FilterContextProvider value={{ trans, popup, filterDataHook, rootProps }}>
                <FilterColumnsTags filterItems={filterDataHook.getData()} remove={true} />
                {popup.render()}
            </FilterContextProvider>
        )
    }
    return { renderButton, renderTags }
}

const FilterButton: FC<{ attrs?: any }> = ({ attrs }) => {
    const { popup, trans, rootProps } = useFilterContext()
    const { filter = {} } = rootProps;
    const { modalAttrs } = filter
    const openModal = () => {
        popup.addModal({
            header: { title: trans('filters') },
            position: 'fullscreen', body: <FilterModal />,
            backdropAttrs: { className: 'aio-table-filter-modal aio-table-filter-modal-size' },
            modalAttrs
        })
    }
    const Attrs = UT.AddToAttrs(attrs, { className: 'aio-table-toolbar-button', attrs: { onClick: openModal } })
    return (<button {...Attrs}>{new UT.GetSvg('em').getIcon('mdiFilter', 0.7)}</button>)
}
const FilterColumnsTags: FC<{ filterItems: I_table_filter_item[], remove?: boolean, wrap?: boolean }> = ({ filterItems, remove }) => {
    const { filterDataHook } = useFilterContext()
    const { filterColumns } = useProvider()
    const getTags = () => {
        const res = []
        for (let i = 0; i < filterItems.length; i++) {
            const filterItem = filterItems[i];
            const { columnId, value } = filterItem
            if (value === undefined || value === '' || !columnId) { continue }
            const column = filterColumns.find((o) => o.id === columnId)
            if (!column) { continue }
            const { type = 'text' } = column;
            let valuePresent = value;
            const options = column.filterInput?.options || []
            if (type === 'multiSelect') {
                if (!value || !value.length) { continue }
                const texts = options.filter((o: any) => value.indexOf(o.value) !== -1).map((o: any) => o.text)
                valuePresent = texts.join(' | ')
            }
            else if (type === 'select') {
                const option = options.find((o: any) => o.value === value)
                valuePresent = option?.text || ''
            }
            else if (type === 'date' || type === 'jalaliDate') {
                const dateUnit = column.filterInput?.dateUnit || { year: true, month: true, day: true }
                const jalali = column.type === 'jalaliDate';
                if (dateUnit === 'month') { valuePresent = new AIODate().getDateByPattern(value, '{year}/{month}', jalali) }
                else if (dateUnit === 'day') { valuePresent = new AIODate().getDateByPattern(value, '{year}/{month}/{day}', jalali) }
                else if (dateUnit === 'hour') { valuePresent = new AIODate().getDateByPattern(value, '{year}/{month}/{day} {hour}:00 }', jalali) }
                else if (dateUnit === 'minute') { valuePresent = new AIODate().getDateByPattern(value, '{year}/{month}/{day} {hour}:{minute}', jalali) }
            }
            else if (type === 'number') {
                valuePresent = UT.SplitNumber(valuePresent)
            }
            else if (type === 'checkbox') {
                valuePresent = ''
            }
            res.push(
                <FilterTag
                    key={filterItem.id}
                    column={column} filterItem={filterItem}
                    valuePresent={valuePresent}
                    onRemove={remove ? () => filterDataHook.removeFilterItem(filterItem) : undefined}
                />
            )
        }
        return res
    }
    if (!filterItems.length) { return null }
    return (
        <div className="aio-table-filter-tags">
            {getTags()}
        </div>
    )
}

const FilterTag: FC<{ filterItem: I_table_filter_item, valuePresent: string, column: I_table_column<any>, onRemove?: () => void }> = ({ filterItem, valuePresent, column, onRemove }) => {
    const { trans } = useFilterContext()
    const { operator } = filterItem
    return (
        <div className="aio-table-filter-tag" onClick={onRemove}>
            <div className="aio-table-filter-tag-column">{column.filterInput?.label || column.title || ''}</div>
            {!!operator && <div className="aio-table-filter-tag-operator">{trans(operator)}</div>}
            <div className="aio-table-filter-tag-value">{valuePresent}</div>
            {!!onRemove && new UT.GetSvg('em').getIcon('mdiClose', 0.6)}
        </div>
    )
}
const SaveConfirm: FC<{ onSubmit: (name: string) => void }> = ({ onSubmit }) => {
    const { popup, filterDataHook, trans } = useFilterContext()
    const [name, setName] = useState<string>('')
    return (
        <div className="aio-table-filter-save-modal">
            <AIFormInput
                label={trans('inter filter name')}
                input={(
                    <AIText
                        options={filterDataHook.getSaves()}
                        option={{ text: (o) => o.name, value: (o) => o.name, onClick: (o) => setName(o) }}
                        onChange={(newName, option) => setName(option ? option.text : newName)}
                    />
                )}
            />
            <div className="aio-table-filter-save-modal-footer">
                <button className="aio-table-filter-button aio-table-filter-active-button" disabled={!name} onClick={() => onSubmit(name)}>
                    {trans(name === filterDataHook.activeName ? 'edit' : 'save')}
                </button>
                <button className="aio-table-filter-button" onClick={() => popup.removeModal()}>{trans('cansel')}</button>
            </div>
        </div>
    )
}
const FilterSaveds: FC = () => {
    const { trans, filterDataHook } = useFilterContext();
    const saves = filterDataHook.getSaves()
    if (!saves.length) { return (<div className="aio-table-saved-filters-empty">{trans('saved empty')}</div>) }
    return (<div className="aio-table-saved-filters">{saves.map((o) => <FilterSaved key={o.name} item={o} />)}</div>)
}
const FilterSaved: FC<{ item: I_table_filter_saved }> = ({ item }) => {
    const { rootProps } = useProvider()
    const { trans, filterDataHook } = useFilterContext();
    const { fa } = rootProps;
    if (!item.name) { return null }
    const header_layout = () => <div className="aio-table-saved-filter-header"> {name_layout()} {remove_layout()} {activate_layout()}</div>
    const name_layout = () => <div className="aio-table-saved-filter-name">{item.name}</div>
    const remove_layout = () => {
        const attrs = { className: "aio-table-filter-remove-button", onClick: () => filterDataHook.removeSave(item), style: { [fa ? 'right' : 'left']: -4 } }
        return (<div {...attrs}><FilterRemoveIcon /></div>)
    }
    const activate_layout = () => {
        return (<div className="aio-table-filter-button" onClick={() => filterDataHook.activeSave(item)}><FilterActiveIcon /> {trans('activate')}</div>)
    }
    const body_layout = () => {
        return <div className="aio-table-saved-filter-body"><FilterColumnsTags filterItems={item.data.filter((o: any) => o.value !== undefined && o.value !== '')} /></div>
    }
    return (<div className="aio-table-saved-filter">{header_layout()} {body_layout()}</div>)
}
const FilterModal: FC = () => {
    const { rootProps } = useProvider();
    const { trans } = useFilterContext()
    const { fa, filter } = rootProps
    const { getSavedItems } = filter || {}
    const collections_layout = () => {
        return (
            <div className="aio-table-filter-panel">
                <div className="aio-table-filter-panel-header">
                    <div className="aio-table-filter-panel-label">{trans('saved filters')}</div>
                </div>
                <FilterSaveds />
            </div>
        )
    }
    return (
        <div className={`aio-table-filter-body aio-table-filter-body-${fa ? 'rtl' : 'ltr'}`}>
            <CurrentFilters />
            {!!getSavedItems && collections_layout()}
        </div>
    )
}
const CurrentFilters: FC = () => {
    const { rootProps } = useProvider();
    const { filterDataHook, trans } = useFilterContext()
    const { filter } = rootProps;
    const canSave = (!!filter && !!filter.setSavedItems)
    const data = filterDataHook.getData()
    return (
        <div className="aio-table-filter-panel">
            <div className="aio-table-filter-panel-header">
                <div className="aio-table-filter-panel-label">{trans('current filters')}</div>
                <button className="aio-table-filter-button" onClick={filterDataHook.addColumnsFilterItem}><FilterAddIcon /> {trans('add')}</button>
                {!!canSave && !!data.length && <button className="aio-table-filter-button" onClick={filterDataHook.save}><FilterSaveIcon /> {trans('save')}</button>}
            </div>
            <div className="aio-table-current-filters">
                {data.map((columnsFilterItem: any) => <CurrentFilter key={columnsFilterItem.id} filterItem={columnsFilterItem} />)}
            </div>
        </div>
    )
}
const CurrentFilter: FC<{ filterItem: I_table_filter_item }> = ({ filterItem }) => {
    const { operator, columnId, value } = filterItem;
    const { filterDataHook, trans } = useFilterContext()
    const { filterColumns } = useProvider()
    const [column] = useState<I_table_column<any> | undefined>(getColumn)
    const [operators, setOperators] = useState<('less' | 'lessequal' | 'more' | 'moreequal' | 'equal' | 'notEqual' | 'contain' | 'notContain')[]>(getOperators)
    function getColumn() { return filterColumns.find((o) => o.id === columnId) }
    useEffect(() => {
        setOperators(getOperators())
    }, [JSON.stringify(filterItem.columnId)])
    function getOperators() {
        if (!column) { return [] }
        const res: ('less' | 'lessequal' | 'more' | 'moreequal' | 'equal' | 'notEqual' | 'contain' | 'notContain')[] = []
        const operators: ('less' | 'lessequal' | 'more' | 'moreequal' | 'equal' | 'notEqual' | 'contain' | 'notContain')[] = ['less', 'more', 'equal', 'notEqual', 'contain', 'notContain']
        const dic: { [key in 'text' | 'number' | 'date' | 'jalaliDate' | 'select' | 'multiSelect' | 'checkbox']: { [key in ('less' | 'lessequal' | 'more' | 'moreequal' | 'equal' | 'notEqual' | 'contain' | 'notContain')]?: boolean } } = {
            text: { contain: true, notContain: true, equal: true, notEqual: true },
            number: { less: true, more: true, lessequal: true, moreequal: true, equal: true, notEqual: true },
            date: { less: true, more: true, lessequal: true, moreequal: true, equal: true, notEqual: true },
            jalaliDate: { less: true, more: true, lessequal: true, moreequal: true, equal: true, notEqual: true },
            select: { equal: true, notEqual: true },
            multiSelect: { contain: true, notContain: true },
            checkbox: {}
        }
        for (let i = 0; i < operators.length; i++) {
            const o = operators[i];
            if (!dic[column.type || 'text'][o]) { continue }
            res.push(operators[i])
        }
        return res
    }
    const changeValue = (newValue: any) => {
        filterDataHook.changeColumnsFilterItem({ filterItem: filterItem, key: 'value', value: newValue, column })
    }
    const getValueInput = () => {
        if (!column) { return null }
        const type = column.filterInput?.type || column.type || 'text'
        if (type === 'select') {
            return (<AISelect {...column.filterInput} value={value} onChange={(value) => changeValue(value)} deSelect={true} popover={{ fitHorizontal: true, ...column.filterInput?.popover }} />)
        }
        if (type === 'multiSelect') {
            return (<AIRadio multiple={true}  {...column.filterInput} value={value} onChange={(value) => changeValue(value)} deSelect={true} />)
        }
        if (type === 'text') {
            return <AIText {...column.filterInput} value={value} onChange={(value) => changeValue(value)} deSelect={true} />
        }
        if (type === 'checkbox') {
            return <AICheckbox {...column.filterInput} value={value} onChange={(value) => changeValue(value)} deSelect={true} />
        }
        if (type === 'number') {
            return <AINumber {...column.filterInput} value={value} onChange={(value) => changeValue(value)} deSelect={true} inputAttrs={{ style: { letterSpacing: 4 } }} justify={true} />
        }
        if (type === 'date' || type === 'jalaliDate') {
            return <TimeInput {...column.filterInput} filterItem={filterItem} onChange={(value) => changeValue(value)} column={column} jalali={type === 'jalaliDate'} />
        }
    }
    return (
        <>
            <div className="aio-table-filter-row">
                {
                    !!column &&
                    <AISelect
                        className='msf'
                        options={filterColumns.map((o) => ({ text: o.title, value: o.id }))} value={columnId}
                        onChange={(columnId) => filterDataHook.changeColumnsFilterItem({ filterItem: filterItem, key: 'columnId', value: columnId, column })}
                        popover={{ fitHorizontal: true }}
                    />
                }
                {
                    !!column && !!operators.length &&
                    <AISelect
                        className='aio-table-filter-operator-select'
                        options={operators} value={operator} option={{ text: (operator) => trans(operator), value: (operator) => operator }}
                        onChange={(operator) => filterDataHook.changeColumnsFilterItem({ filterItem: filterItem, key: 'operator', value: operator, column })}
                        popover={{ fitHorizontal: true }}
                    />
                }
                {getValueInput()}
                <div className="aio-table-filter-remove-button" onClick={() => filterDataHook.removeFilterItem(filterItem)}><FilterRemoveIcon /></div>
            </div>
        </>
    )
}
const TimeInput: FC<{ filterItem: I_table_filter_item, onChange: (newValue: any) => void, column: I_table_column<any>, jalali: boolean }> = ({ filterItem, onChange, column, jalali }) => {
    const [unit] = useState<any>(getUnit)
    function getUnit() {
        const { dateUnit = 'day' } = column.filterInput || {};
        const res: any = { year: true, month: true }
        if (dateUnit !== 'month') {
            res.day = true;
            if (dateUnit !== 'day') {
                res.hour = true;
                if (dateUnit !== 'hour') {
                    res.minute = true
                }
            }
        }
        return res
    }
    const DATE = new AIODate()
    return (<AITime jalali={jalali} timeUnit={unit} value={filterItem.value} deSelect={true} onChange={(value) => onChange(value ? DATE.getTime(value) : undefined)} />)
}
const FilterSaveIcon: FC = () => new UT.GetSvg('em').getIcon('mdiSaveContent', 0.8)
const FilterRemoveIcon: FC = () => new UT.GetSvg('em').getIcon('mdiClose', 0.6)
const FilterActiveIcon: FC = () => new UT.GetSvg('em').getIcon('mdiCheckBold', 0.6)
const FilterAddIcon: FC = () => new UT.GetSvg('em').getIcon('mdiPlusThick', 0.7)
const useFilterData = (popup: I_usePopup, trans: I_table_filterTrans) => {
    const [activeName] = useState<string | undefined>()
    const { filterColumns, rootProps } = useProvider();
    const { filter = {} } = rootProps;
    const [saves, setSaves] = useState<I_table_filter_saved[]>([])
    const savesRef = useRef<I_table_filter_saved[]>(saves)
    savesRef.current = saves
    const getSaves = () => savesRef.current;
    const [data, setData] = useState<any>(getInitialData)
    function getInitialData(): any {
        if (filter.cache) {
            const storage = new UT.Storage(filter.cache);
            const data = storage.load('columnsFilterData', filter.items || [])
            if (filter.onChange) { filter.onChange(data) }
            return data
        }
        else {
            return filter.items || []
        }
    }
    const getData = () => dataRef.current
    const dataRef = useRef<any>(data)
    dataRef.current = data
    const changeData = async (newData: any) => {
        if (filter.onChange) {
            const res = await filter.onChange(newData)
            if (res === false) { return false }
        }
        setData(newData);
        return true;
    }
    const changeSaves = async (newSaves: any[]): Promise<boolean> => {
        const { setSavedItems } = filter;
        if (!setSavedItems) { return false }
        const res = await setSavedItems(newSaves);
        if (res === false) { return false }
        setSaves(newSaves);
        return true
    }
    const save = () => {
        popup.addModal({
            header: { title: trans('save filter') }, position: 'center',
            body: (
                <SaveConfirm
                    onSubmit={async (name) => {
                        if (!name) { return }
                        const data = dataRef.current
                        const saves = savesRef.current;
                        const newSave = { name, data }
                        const isExist = !!saves.find((o) => o.name === name)
                        let newSaves: any[] = []
                        if (isExist) { newSaves = saves.map((o) => o.name === name ? newSave : o) }
                        else { newSaves = [...saves, newSave] }
                        const res = await changeSaves(newSaves);
                        if (res) { popup.removeModal(); }
                    }}
                />
            ),
            backdropAttrs: { className: 'aio-table-filter-modal' }
        })
    }
    const removeSave = (saveItem: I_table_filter_saved) => {
        popup.addConfirm({
            title: trans('remove saved filter'),
            text: trans('remove confirm'),
            submitText: trans('remove'),
            canselText: trans('cansel'),
            backdropAttrs: { className: 'aio-table-filter-modal' },
            submitAttrs: { className: 'aio-table-filter-button aio-table-filter-active-button' },
            canselAttrs: { className: 'aio-table-filter-button' },
            onSubmit: async () => await changeSaves(savesRef.current.filter((o) => o.name !== saveItem.name))
        })
    }
    const activeSave = async (saveItem: I_table_filter_saved) => {
        const { onChange } = filter
        if (!onChange) { return false }
        changeData(saveItem.data)
    }
    const fetchSaves = async () => {
        const { getSavedItems } = filter;
        if (!getSavedItems) { return }
        const saves = await getSavedItems()
        if (saves) { setSaves(saves) }
    }
    useEffect(() => { fetchSaves() }, [])
    const getDefaultFilter = (column: I_table_column<any>): I_table_filter_item | false => {
        const { type = 'text' } = column;
        const { options } = column?.filterInput || {}
        let value = '';
        if (type === 'select' && !!options && !!options.length) { value = options[0].value }
        const operatorDic: { [key in NonNullable<I_table_column<any>["type"]>]: ('less' | 'lessequal' | 'more' | 'moreequal' | 'equal' | 'notEqual' | 'contain' | 'notContain' | false) } = {
            text: 'contain', number: 'equal', date: 'equal', jalaliDate: 'equal', select: 'equal', multiSelect: 'contain', checkbox: false
        }
        const operator = column ? (operatorDic[type] === false ? undefined : operatorDic[type]) : undefined
        return { value, operator, columnId: column?.id, id: 'a' + UT.GetRandomNumber(10000000, 99999999) }
    }
    const changeColumnsFilterItem = (p: { filterItem: I_table_filter_item, key: keyof I_table_filter_item, value: any, column?: I_table_column<any> }) => {
        const { key, value, filterItem, column } = p;
        const columnsFilterItems = dataRef.current;
        let newFilterItem: any;
        if (!!column && key === 'columnId') {
            const defaultFilter = getDefaultFilter(column);
            if (defaultFilter === false) { return }
            newFilterItem = { ...defaultFilter, [key]: value }
        }
        else { newFilterItem = { ...filterItem, [key]: value } }
        const newFilterItems = columnsFilterItems.map((o: any) => o.id === filterItem.id ? newFilterItem : o)
        changeData(newFilterItems)
    }
    const addColumnsFilterItem = () => {
        const column = filterColumns[0];
        if (!column) { return }
        const defaultFilter = getDefaultFilter(column)
        if (defaultFilter === false) { return }
        const columnsFilterItems = dataRef.current;
        changeData([...columnsFilterItems, defaultFilter])
    }
    const removeFilterItem = (filterItem: I_table_filter_item) => {
        const columnsFilterItems = dataRef.current;
        changeData(columnsFilterItems.filter((o: any) => !!o && (o.id !== filterItem.id)))
    }
    return { getSaves, getData, save, removeSave, removeFilterItem, changeColumnsFilterItem, activeName, activeSave, addColumnsFilterItem }
}

type I_groupRow = { groupKey: string,groupValue:any, groups?: I_groupRow[], rows?: any[],isLeaf:boolean }
type I_groupData = { groupKey: string,groupValue:any, groups?: I_groupRow[], rows?: any[], level: number, open: boolean, toggle: () => void, groupKeys: string[],groupValues:any[], rtl: boolean, toggleIcon: ReactNode }
const TableGroups: FC<{ data: I_groupRow[], level: number, groupKeys: string[],groupValues:any[],isParentLastChild:boolean }> = ({ data, level, groupKeys,groupValues }) => {
    const { groupsHook, rootProps } = useProvider()
    const { fa, group } = rootProps;
    if (!group) { return null }
    return (<>{
        data.map((gr,i) => {
            const {groupKey,groupValue,groups,rows} = gr;
            const newGroupKeys = [...groupKeys, groupKey]
            const newGroupValues = [...groupValues, groupValue]
            const openKey = newGroupKeys.toString()
            const open = groupsHook.groupsOpen[openKey] === true
            const rtl = !!fa
            const toggle = () => groupsHook.toggle(openKey)
            const isLastChild = i === data.length - 1
            const toggleIcon = (
                <div className="aio-table-group-toggle-icon" onClick={toggle}>
                    {new UT.GetSvg().getIcon(open ? 'mdiChevronDown' : rtl ? 'mdiChevronLeft' : 'mdiChevronRight', 0.8)}
                </div>
            )
            const groupData: I_groupData = { groups,rows,groupKey,groupValue, level, open, toggle, groupKeys: newGroupKeys,groupValues:newGroupValues, rtl, toggleIcon }
            const card = group.getCard(groupData)
            return (
                <TableGroup
                    key={openKey}
                    groupData={groupData}
                    card={card || undefined}
                    isLastChild={isLastChild}
                />
            )
        })
    }</>)
}
const TableGroup: FC<{ groupData: I_groupData, card?: ReactNode,isLastChild:boolean }> = ({ groupData, card,isLastChild }) => {
    const { groups, rows, groupKey,level, groupKeys,groupValues } = groupData
    return (
        <>
            <TableGroupRow groupData={groupData} card={card} />
            {
                !!groupData.open &&
                <>
                    {!!groups && <TableGroups key={groupKey} data={groups} level={level + 1} groupKeys={groupKeys} groupValues={groupValues} isParentLastChild={isLastChild} />}
                    {!!rows && <TableRows key={groupKey} rows={rows} />}
                </>
            }
        </>
    )
}
const TableGroupRow: FC<{ groupData: I_groupData, card: ReactNode | string }> = ({ groupData: g, card }) => {
    if (typeof card === 'string' || typeof card === 'number') {
        return (
            <div className="aio-table-group-row" style={{ [g.rtl ? 'paddingRight' : 'paddingLeft']: (g.level + 1) * 12 }}>
                {g.toggleIcon}
                <div className="aio-table-group-title">{card}</div>
            </div>
        )
    }
    else { return card }
}

const useGroups = <T,>(group: I_table<T>["group"]) => {
    const [groupsOpen, setGroupsOpen] = useState<I_groupsOpen>({})
    function getGroupedRows(rows: any[]) {
        if (!group) { return }
        const res: I_groupRow[] = []
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const groups = group.getKeyValues(row)
            let parent: I_groupRow = {} as any;
            for (let j = 0; j < groups.length; j++) {
                const {groupKey,groupValue} = groups[j];
                const isRoot = j === 0;
                const isLeaf = j === groups.length - 1;
                const parentResult = (isRoot ? res : (parent as any).groups).find((o: any) => o.groupKey === groupKey);
                if (!parentResult) {
                    const group: I_groupRow = { groupKey,groupValue,isLeaf }
                    if (isLeaf) { group.rows = [] }
                    else { group.groups = [] }
                    (isRoot ? res : (parent as any).groups).push(group)
                    parent = group
                }
                else { parent = parentResult }
                if (isLeaf) {
                    (parent as any).rows.push(row)
                }
            }
        }
        return res
    }
    const toggle = (openKey: string) => setGroupsOpen({ ...groupsOpen, [openKey]: !groupsOpen[openKey] })
    return { groupsOpen, setGroupsOpen, getGroupedRows, toggle }
}
